/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Service from 'types/service';
import { computeSavedEntityState } from 'state/utils/computeSavedEntityState';
import { isEmpty } from 'es-toolkit/compat';
import { todayBusinessDay } from '@tock/shared/utils/time';
import AppConstants from 'constants/AppConstants';
import ServerConnectionState from 'constants/service/serverConnectionState';

export interface ServiceStateAppFloorplan {
  id: number;
  activeTable: Service.TableFromElm[];
  archivedTable: Service.TableFromElm[];
  tableCombination: Service.TableCombination[];
}

export interface ServiceStateApp {
  availableTableTags: Dictionary<string[]>;
  serverIdToPartyIds: Dictionary<number[]>;
  floorplan?: ServiceStateAppFloorplan;
  partyIdToTableId: Dictionary<number[]>;
  tables: NumericDictionary<Service.ServiceTable>;
  parties: NumericDictionary<Service.PartyFromElm>;
  patronIdToPartyIds: NumericDictionary<number[]>;
  businessTag: Dictionary<BusinessTag>;
  handleDuplicateAddParty: boolean;
}

export interface BusinessTag {
  id: number;
  text: string;
  isPriority: boolean;
  type: string;
  isArchived: boolean;
  ordering: number;
  icon: string;
}

/**
 * New types that give us stricter type safety as we migrate our Elm code over to TypeScript.
 * Most notably, these types know that when you look up a key in a dictionary, the resulting
 * value will be undefined if the key isn't present, and TypeScript will force you to handle
 * this error case.
 *
 * We eventually want these "strict" types to be the standard across the whole app, but that
 * would require changing a bunch of code, so we're in an interm state where we have both strict
 * and loose types.
 */
export interface ServiceStateAppStrict
  extends Omit<
    ServiceStateApp,
    'tables' | 'parties' | 'partyIdToTableId' | 'serverIdToPartyIds' | 'patronIdToPartyIds'
  > {
  tables: DictionaryStrict<Service.TableFromElm>;
  parties: DictionaryStrict<Service.PartyFromElm>;
  patronIdToPartyIds: DictionaryStrict<number[]>;
  partyIdToTableId: DictionaryStrict<number[]>;
  serverIdToPartyIds: DictionaryStrict<number[]>;
}

export interface ServiceStateHistory {
  isLoading: boolean;
  isVisible: boolean;
  serverHasMoreHistory: boolean;
  items: Service.HistoryItem[];
  earliestServerId: number;
}

export interface ServiceState {
  currentTopic?: Service.Topic;
  communicator?: any;
  serverConnectionState?: any;
  history?: ServiceStateHistory;
  app?: ServiceStateApp;
  isReadOnly?: boolean;
  historyToggledOn?: boolean;
  decodeError?: string | null;
  error?: any;
}

// TODO: maybe use Required utility type - https://www.typescriptlang.org/docs/handbook/utility-types.html#requiredt
export interface ServiceStateStrict
  extends Omit<ServiceState, 'app' | 'history' | 'historyToggledOn' | 'serverConnectionState'> {
  app: ServiceStateAppStrict;
  history: ServiceStateHistory;
  historyToggledOn: boolean;
  serverConnectionState: keyof typeof ServerConnectionState;
  nextInternalHistoryId: number;
}

interface SavedState {
  [date: string]: ServiceState;
}

export let savedState: SavedState = {};

export default function app(state: ServiceState = {}, action: BaseTockAction): ServiceState {
  const { type, payload } = action;
  switch (type) {
    case 'APP_STATE_CHANGED':
      return payload;

    case AppConstants.CALENDAR_ENTITY_SAVE_DATE: {
      if (!isEmpty(state)) {
        const stateToSave: ServiceState = state;
        const newSavedState: SavedState = computeSavedEntityState<ServiceState>({
          newDate: payload,
          stateToSave,
          savedState: savedState,
          todayString: todayBusinessDay().toString(),
        });

        savedState = newSavedState;
      }
      return state;
    }
    case AppConstants.CALENDAR_ENTITY_RESTORE_DATE: {
      const restoredState = savedState[payload];
      if (!restoredState || isEmpty(restoredState)) {
        return state;
      }
      return restoredState;
    }
    default:
      return state;
  }
}
